import { Component, DestroyRef, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { Tooltip } from 'primeng/tooltip';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { catchError, of } from 'rxjs';
import {
  CreatePresalesActivityDto,
  PresalesActivity,
  PresalesActivityService,
} from '../../services/presales-activity.service';
import { AuthService, UserInfo } from '../../services/auth.service';
import { EmployeeService } from '../../services/employee.service';
import { Employee } from '../../models/employee.model';
import { PRESALES_PROGRESS_OPTIONS } from '../../models/presales.model';

@Component({
  selector: 'app-presales-activity',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Dialog,
    InputText,
    Select,
    DatePicker,
    Tooltip,
    ConfirmDialog,
  ],
  templateUrl: './presales-activity.component.html',
  styleUrl: './presales-activity.component.scss',
  providers: [ConfirmationService],
})
export class PresalesActivityComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);

  @Input() presalesProjectId: string = '';

  items: PresalesActivity[] = [];
  selected: PresalesActivity | null = null;
  activityDate: Date | null = null;
  displayDialog = false;
  isEditMode = false;
  loading = false;
  saveInFlight = false;
  fieldTouched: Record<string, boolean> = {};
  user: UserInfo | null = null;

  /** Picker binds a Date; the model holds the ISO string the API expects. */
  targetDate: Date | null = null;

  employees: Employee[] = [];

  /** Matches the presales board's columns, so an activity and a deal read the same way. */
  progressOptions = PRESALES_PROGRESS_OPTIONS;

  /**
   * Quarter steps instead of a 0–100 spinner. Unlike the project Tasks form there is no
   * server rule tying 100% to a particular state here, so the full range is offered.
   * Declared once so the reference stays stable between change-detection cycles.
   */
  readonly percentOptions: { label: string; value: number }[] = [
    { label: '0%', value: 0 },
    { label: '25%', value: 25 },
    { label: '50%', value: 50 },
    { label: '75%', value: 75 },
    { label: '100%', value: 100 },
  ];

  get employeeOptions(): { label: string; value: string }[] {
    return this.employees
      .map((e) => ({ label: e.name, value: e.id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  constructor(
    private activityService: PresalesActivityService,
    private authService: AuthService,
    private employeeService: EmployeeService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit() {
    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((u) => (this.user = u));

    // Owner picker. Inactive staff are left out so a new activity cannot be assigned to
    // someone who has left; an existing row keeps its stored OwnerName either way.
    this.employeeService
      .getAllEmployees()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of([] as Employee[])),
      )
      .subscribe((list) => (this.employees = (list ?? []).filter((e) => e.isActive)));

    if (this.presalesProjectId) this.load();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['presalesProjectId'] && this.presalesProjectId) this.load();
  }

  load() {
    if (!this.presalesProjectId) return;
    this.loading = true;
    this.activityService
      .getByPresalesProjectId(this.presalesProjectId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((err) => {
          if (err?.status !== 404) {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to load activities.',
            });
          }
          return of([] as PresalesActivity[]);
        }),
      )
      .subscribe((items) => {
        this.items = items;
        this.loading = false;
      });
  }

  showAddDialog() {
    if (!this.presalesProjectId) return;
    this.selected = {
      id: '',
      presalesProjectId: this.presalesProjectId,
      // Type is no longer asked for — the form leads with the name instead. Every new
      // activity is a Note, which is what all but a handful ever were; the column still
      // renders whatever older rows carry.
      type: 'Note',
      title: '',
      description: '',
      activityDate: this.toIsoDate(new Date()),
      authorName: this.user?.displayName,
      authorEmail: this.user?.email,
      targetDate: null,
      ownerId: null,
      progress: null,
      progressPercentage: 0,
    };
    // Stamped today, date only. Still editable, for logging something after the fact.
    this.activityDate = new Date();
    this.targetDate = null;
    this.fieldTouched = {};
    this.isEditMode = false;
    this.displayDialog = true;
  }

  showEditDialog(item: PresalesActivity) {
    this.selected = { ...item };
    // Snapped to the nearest option the dropdown offers: older rows can hold any value,
    // which would leave the control blank and reset to 0 on the next save.
    this.selected.progressPercentage =
      Math.round(Math.min(Math.max(item.progressPercentage ?? 0, 0), 100) / 25) * 25;
    this.activityDate = item.activityDate ? new Date(item.activityDate) : new Date();
    this.targetDate = item.targetDate ? new Date(item.targetDate) : null;
    this.fieldTouched = {};
    this.isEditMode = true;
    this.displayDialog = true;
  }

  save() {
    if (this.saveInFlight) return;
    this.fieldTouched['title'] = true;
    if (!this.selected || !this.selected.title?.trim()) return;

    // Date-only, like the target date below: the time of day was never used, and sending a
    // full timestamp let a timezone offset shift the date by a day.
    this.selected.activityDate = this.toIsoDate(this.activityDate ?? new Date());

    // Date-only, so a timezone offset cannot shift a target date by a day.
    this.selected.targetDate = this.targetDate ? this.toIsoDate(this.targetDate) : null;

    this.saveInFlight = true;

    if (this.isEditMode) {
      this.activityService
        .updateItem(this.selected.id, this.selected)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (updated) => {
            this.saveInFlight = false;
            const idx = this.items.findIndex((i) => i.id === updated.id);
            if (idx !== -1) this.items[idx] = updated;
            this.items = [...this.items].sort(
              (a, b) => new Date(b.activityDate).getTime() - new Date(a.activityDate).getTime(),
            );
            this.messageService.add({
              severity: 'success',
              summary: 'Updated',
              detail: 'Activity updated.',
            });
            this.displayDialog = false;
            this.selected = null;
          },
          error: (err) => {
            this.saveInFlight = false;
            const detail =
              err?.error?.details || err?.error?.message || err?.error?.title || 'Failed to update activity.';
            this.messageService.add({ severity: 'error', summary: 'Error', detail });
          },
        });
    } else {
      const dto: CreatePresalesActivityDto = {
        presalesProjectId: this.selected.presalesProjectId,
        type: this.selected.type,
        title: this.selected.title,
        description: this.selected.description,
        activityDate: this.selected.activityDate,
        authorName: this.selected.authorName,
        authorEmail: this.selected.authorEmail,
        // The follow-up fields were missing here, so owner, target date and progress were
        // silently dropped on a NEW activity and only saved once it was edited again.
        targetDate: this.selected.targetDate,
        ownerId: this.selected.ownerId,
        progress: this.selected.progress,
        progressPercentage: this.selected.progressPercentage,
      };
      this.activityService
        .addItem(dto)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (created) => {
            this.saveInFlight = false;
            this.items = [created, ...this.items].sort(
              (a, b) => new Date(b.activityDate).getTime() - new Date(a.activityDate).getTime(),
            );
            this.messageService.add({
              severity: 'success',
              summary: 'Added',
              detail: 'Activity added.',
            });
            this.displayDialog = false;
            this.selected = null;
          },
          error: (err) => {
            this.saveInFlight = false;
            const detail =
              err?.error?.details || err?.error?.message || err?.error?.title || 'Failed to add activity.';
            this.messageService.add({ severity: 'error', summary: 'Error', detail });
          },
        });
    }
  }

  confirmDelete(item: PresalesActivity) {
    this.confirmationService.confirm({
      header: 'Delete activity?',
      message: `"${item.title}" will be removed permanently.`,
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteItem(item),
    });
  }

  private deleteItem(item: PresalesActivity) {
    this.activityService
      .deleteItem(item.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.items = this.items.filter((i) => i.id !== item.id);
          this.messageService.add({
            severity: 'success',
            summary: 'Deleted',
            detail: 'Activity removed.',
          });
        },
        error: (err) => {
          const detail =
            err?.error?.details || err?.error?.message || err?.error?.title || 'Failed to delete activity.';
          this.messageService.add({ severity: 'error', summary: 'Error', detail });
        },
      });
  }

  formatDateTime(iso?: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /** Date without a time — target dates and created dates are days, not moments. */
  formatDate(iso?: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  /** Label for a stored progress value; 'InProgress' reads as 'In Progress'. */
  progressLabel(progress?: string | null): string {
    if (!progress) return '—';
    return this.progressOptions.find((o) => o.value === progress)?.label ?? progress;
  }

  /**
   * Days a target date is past, or null when it is not overdue. Closed work never is, and
   * an entry with no target date has nothing to be late for.
   */
  overdueDays(item: PresalesActivity): number | null {
    if (!item.targetDate || item.progress === 'Closed') return null;
    const target = new Date(item.targetDate);
    if (isNaN(target.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    const days = Math.floor((today.getTime() - target.getTime()) / 86_400_000);
    return days > 0 ? days : null;
  }

  /** Date-only ISO, so a timezone offset cannot shift a target date by a day. */
  private toIsoDate(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
